// SnakeGame.jsx
import React, { useEffect, useRef, useState } from "react";

const BOARD_SIZE = 20;
const CELL_SIZE = 20;
const INITIAL_SPEED = 180;

export default function SnakeGame() {
    const [snake, setSnake] = useState([
        [10, 10],
        [9, 10],
        [8, 10],
    ]);

    const [food, setFood] = useState([15, 10]);
    const [direction, setDirection] = useState("RIGHT");
    const [gameOver, setGameOver] = useState(false);
    const [paused, setPaused] = useState(false);
    const [score, setScore] = useState(0);

    const directionRef = useRef(direction);
    const touchStart = useRef({ x: 0, y: 0 });

    // récord
    const [highScore, setHighScore] = useState(
        Number(localStorage.getItem("snakeHighScore")) || 0
    );

    useEffect(() => {
        directionRef.current = direction;
    }, [direction]);

    const generateFood = (currentSnake) => {
        let newFood;

        do {
            newFood = [
                Math.floor(Math.random() * BOARD_SIZE),
                Math.floor(Math.random() * BOARD_SIZE),
            ];
        } while (
            currentSnake.some(
                (segment) =>
                    segment[0] === newFood[0] && segment[1] === newFood[1]
            )
        );

        return newFood;
    };

    // teclado
    useEffect(() => {
        const handleKeyDown = (e) => {
            changeDirectionByKey(e.key);
        };

        window.addEventListener("keydown", handleKeyDown);

        return () => window.removeEventListener("keydown", handleKeyDown);
    }, []);

    const changeDirectionByKey = (key) => {
        switch (key) {
            case "ArrowUp":
                if (directionRef.current !== "DOWN") setDirection("UP");
                break;

            case "ArrowDown":
                if (directionRef.current !== "UP") setDirection("DOWN");
                break;

            case "ArrowLeft":
                if (directionRef.current !== "RIGHT") setDirection("LEFT");
                break;

            case "ArrowRight":
                if (directionRef.current !== "LEFT") setDirection("RIGHT");
                break;

            default:
                break;
        }
    };

    // velocidad aumenta con el puntaje
    const speed = Math.max(70, INITIAL_SPEED - score * 5);

    useEffect(() => {
        if (gameOver || paused) return;

        const interval = setInterval(moveSnake, speed);

        return () => clearInterval(interval);
    }, [snake, gameOver, paused, speed]);

    const moveSnake = () => {
        setSnake((prevSnake) => {
            const head = [...prevSnake[0]];

            switch (directionRef.current) {
                case "UP":
                    head[1]--;
                    break;
                case "DOWN":
                    head[1]++;
                    break;
                case "LEFT":
                    head[0]--;
                    break;
                case "RIGHT":
                    head[0]++;
                    break;
            }

            // pared
            if (
                head[0] < 0 ||
                head[0] >= BOARD_SIZE ||
                head[1] < 0 ||
                head[1] >= BOARD_SIZE
            ) {
                setGameOver(true);
                return prevSnake;
            }

            // cuerpo
            if (
                prevSnake.some(
                    (segment) =>
                        segment[0] === head[0] && segment[1] === head[1]
                )
            ) {
                setGameOver(true);
                return prevSnake;
            }

            const newSnake = [head, ...prevSnake];

            // comida
            if (head[0] === food[0] && head[1] === food[1]) {
                const newScore = score + 1;
                setScore(newScore);

                if (newScore > highScore) {
                    setHighScore(newScore);
                    localStorage.setItem("snakeHighScore", newScore);
                }

                setFood(generateFood(newSnake));
            } else {
                newSnake.pop();
            }

            return newSnake;
        });
    };

    const resetGame = () => {
        setSnake([
            [10, 10],
            [9, 10],
            [8, 10],
        ]);
        setFood([15, 10]);
        setDirection("RIGHT");
        setScore(0);
        setGameOver(false);
        setPaused(false);
    };

    // gestos táctiles
    const handleTouchStart = (e) => {
        touchStart.current = {
            x: e.touches[0].clientX,
            y: e.touches[0].clientY,
        };
    };

    const handleTouchEnd = (e) => {
        const deltaX =
            e.changedTouches[0].clientX - touchStart.current.x;

        const deltaY =
            e.changedTouches[0].clientY - touchStart.current.y;

        if (Math.abs(deltaX) > Math.abs(deltaY)) {
            if (deltaX > 30 && directionRef.current !== "LEFT")
                setDirection("RIGHT");

            if (deltaX < -30 && directionRef.current !== "RIGHT")
                setDirection("LEFT");
        } else {
            if (deltaY > 30 && directionRef.current !== "UP")
                setDirection("DOWN");

            if (deltaY < -30 && directionRef.current !== "DOWN")
                setDirection("UP");
        }
    };

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                fontFamily: "Arial",
            }}
        >
            <h1>🐍 Snake (Beta)</h1>

            <h2>Puntos: {score}</h2>

            <h3>🏆 Récord: {highScore}</h3>

            <div
                onTouchStart={handleTouchStart}
                onTouchEnd={handleTouchEnd}
                style={{
                    width: BOARD_SIZE * CELL_SIZE,
                    height: BOARD_SIZE * CELL_SIZE,
                    position: "relative",
                    background: "#111",
                    border: "3px solid #00ff99",
                    borderRadius: 10,
                }}
            >
                {/* comida */}
                <div
                    style={{
                        position: "absolute",
                        width: CELL_SIZE,
                        height: CELL_SIZE,
                        background: "red",
                        borderRadius: "50%",
                        left: food[0] * CELL_SIZE,
                        top: food[1] * CELL_SIZE,
                    }}
                />

                {/* serpiente */}
                {snake.map((segment, index) => (
                    <div
                        key={index}
                        style={{
                            position: "absolute",
                            width: CELL_SIZE,
                            height: CELL_SIZE,
                            left: segment[0] * CELL_SIZE,
                            top: segment[1] * CELL_SIZE,
                            background:
                                index === 0 ? "#00ff99" : "#39ff14",
                            borderRadius: 5,
                        }}
                    />
                ))}

                {gameOver && (
                    <div
                        style={{
                            position: "absolute",
                            inset: 0,
                            background: "rgba(0,0,0,.8)",
                            color: "white",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            fontSize: 32,
                            fontWeight: "bold",
                        }}
                    >
                        GAME OVER
                    </div>
                )}
            </div>

            <div
                style={{
                    marginTop: 20,
                    display: "flex",
                    gap: 10,
                }}
            >
                <button onClick={resetGame}>
                    Reiniciar
                </button>

                <button
                    onClick={() => setPaused(!paused)}
                >
                    {paused ? "Continuar" : "Pausar"}
                </button>
            </div>
        </div>
    );
}